import { HelpCircle } from 'lucide-react'
import { useCallback, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Box, IconButton } from '@mui/material'

const DRLP_TOOLTIP =
  'This is the Designated Responsible Licensed Producer for the state.'

const TOOLTIP_WIDTH_PX = 224
const VIEWPORT_PAD_PX = 8

export function DrlpColumnHeader() {
  const [visible, setVisible] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const tooltipId = useId()

  const updatePosition = useCallback(() => {
    const el = buttonRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const left = Math.max(
      VIEWPORT_PAD_PX,
      Math.min(rect.right - TOOLTIP_WIDTH_PX, window.innerWidth - TOOLTIP_WIDTH_PX - VIEWPORT_PAD_PX),
    )
    setPosition({ top: rect.bottom + 8, left })
  }, [])

  const show = () => {
    updatePosition()
    setVisible(true)
  }

  const hide = () => setVisible(false)

  const toggle = () => {
    if (visible) {
      hide()
      return
    }
    show()
  }

  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
      DRLP?
      <IconButton
        ref={buttonRef}
        type="button"
        size="small"
        aria-label={DRLP_TOOLTIP}
        aria-describedby={visible ? tooltipId : undefined}
        aria-expanded={visible}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={toggle}
        sx={{
          p: 0,
          borderRadius: '2px',
          color: 'rgba(255, 255, 255, 0.9)',
          '&:hover': { color: '#ffffff', backgroundColor: 'transparent' },
          '&:focus': { outline: 'none' },
          '&:focus-visible': {
            outline: 'none',
            boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.8)',
          },
        }}
      >
        <HelpCircle size={14} aria-hidden />
      </IconButton>
      {visible && position
        ? createPortal(
            <Box
              component="span"
              id={tooltipId}
              role="tooltip"
              onMouseEnter={show}
              onMouseLeave={hide}
              sx={{
                position: 'fixed',
                top: position.top,
                left: position.left,
                width: TOOLTIP_WIDTH_PX,
                zIndex: 9999,
                borderRadius: '6px',
                backgroundColor: '#0f172a',
                px: 1.25,
                py: 1,
                textAlign: 'left',
                fontSize: 11,
                fontWeight: 400,
                textTransform: 'none',
                lineHeight: 1.375,
                color: '#ffffff',
                boxShadow: 6,
              }}
            >
              {DRLP_TOOLTIP}
            </Box>,
            document.body,
          )
        : null}
    </Box>
  )
}
